/**
 * Scroll Carousel custom elements for E-sellers Theme.
 * Ported from Impact Theme to ensure visual and functional parity.
 */

function throttle(callback) {
  let requestId = null, lastArgs;
  const later = (context) => () => {
    requestId = null;
    callback.apply(context, lastArgs);
  };
  const throttled = (...args) => {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(later(this));
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}

// Scroll observer helper
class ScrollArea {
  constructor(element, abortController = null) {
    this._element = element;
    this._allowTriggerNearingStartEvent = false;
    this._allowTriggerLeavingStartEvent = true;
    this._allowTriggerNearingEndEvent = true;
    this._allowTriggerLeavingEndEvent = false;
    if (window.ResizeObserver) {
      new ResizeObserver(this._checkIfScrollable.bind(this)).observe(element);
    }
    this._element.addEventListener("scroll", throttle(this._onScroll.bind(this)), { signal: abortController?.signal });
  }
  get scrollNearingThreshold() {
    return 125;
  }
  get scrollDirection() {
    if (this._element.scrollWidth > this._element.clientWidth) {
      return "inline";
    } else if (this._element.scrollHeight > this._element.clientHeight) {
      return "block";
    } else {
      return "none";
    }
  }
  _checkIfScrollable() {
    this._element.classList.toggle("is-scrollable", this.scrollDirection !== "none");
  }
  _onScroll() {
    clearTimeout(this._scrollTimeout);
    this._lastScrollPosition = this._lastScrollPosition ?? (this.scrollDirection === "inline" ? Math.abs(this._element.scrollLeft) : Math.abs(this._element.scrollTop));
    let direction;
    if (this.scrollDirection === "inline") {
      direction = this._lastScrollPosition > Math.abs(this._element.scrollLeft) ? "start" : "end";
      this._lastScrollPosition = Math.abs(this._element.scrollLeft);
    } else {
      direction = this._lastScrollPosition > Math.abs(this._element.scrollTop) ? "start" : "end";
      this._lastScrollPosition = Math.abs(this._element.scrollTop);
    }
    const scrollPosition = Math.round(Math.abs(this.scrollDirection === "inline" ? this._element.scrollLeft : this._element.scrollTop)), scrollMinusSize = Math.round(this.scrollDirection === "inline" ? this._element.scrollWidth - this._element.clientWidth : this._element.scrollHeight - this._element.clientHeight);
    if (direction === "start" && this._allowTriggerNearingStartEvent && scrollPosition <= this.scrollNearingThreshold) {
      this._allowTriggerNearingStartEvent = false;
      this._allowTriggerLeavingStartEvent = true;
      this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "start" } }));
    } else if (direction === "end" && scrollPosition > this.scrollNearingThreshold) {
      this._allowTriggerNearingStartEvent = true;
      if (this._allowTriggerLeavingStartEvent) {
        this._allowTriggerLeavingStartEvent = false;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "start" } }));
      }
    }
    if (direction === "end" && this._allowTriggerNearingEndEvent && scrollMinusSize <= scrollPosition + this.scrollNearingThreshold) {
      this._allowTriggerNearingEndEvent = false;
      this._allowTriggerLeavingEndEvent = true;
      this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "end" } }));
    } else if (direction === "start" && scrollMinusSize > scrollPosition + this.scrollNearingThreshold) {
      this._allowTriggerNearingEndEvent = true;
      if (this._allowTriggerLeavingEndEvent) {
        this._allowTriggerLeavingEndEvent = false;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "end" } }));
      }
    }
    if (window.onscrollend === undefined) {
      this._scrollTimeout = setTimeout(() => {
        this._element.dispatchEvent(new CustomEvent("scrollend", { bubbles: true, composed: true }));
      }, 75);
    }
  }
}

// Scroll progress bar element
class ScrollProgress extends HTMLElement {
  connectedCallback() {
    this._onScroll = throttle(this._updateScrollProgress.bind(this));
    if (this.scrolledElement) {
      this.scrolledElement.addEventListener("scroll", this._onScroll);
      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(this._updateScrollProgress.bind(this));
        this._resizeObserver.observe(this.scrolledElement);
      }
    }
  }
  disconnectedCallback() {
    if (this._onScroll && this.scrolledElement) {
      this.scrolledElement.removeEventListener("scroll", this._onScroll);
      this._onScroll.cancel?.();
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }
  get scrolledElement() {
    return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
  }
  _updateScrollProgress() {
    if (!this.scrolledElement) return;
    const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft);
    const advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
    this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
  }
}
if (!window.customElements.get("scroll-progress")) {
  window.customElements.define("scroll-progress", ScrollProgress);
}

// Base Carousel Element
class BaseCarousel extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._reloaded = false;
    if (Shopify.designMode) {
      this.closest(".shopify-section").addEventListener("shopify:section:select", (event) => this._reloaded = event.detail.load);
    }
    if (this.items.length > 1) {
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target), { animate: !event.detail.load }));
      }
      if (this.hasAttribute("adaptive-height")) {
        this.addEventListener("carousel:settle", this._adjustHeight);
        this._adjustHeight();
      }
      this.addEventListener("carousel:select", this._preloadImages, { signal: this._abortController.signal });
      this.addEventListener("carousel:filter", this._filterItems, { signal: this._abortController.signal });
      this.addEventListener("control:prev", () => this.previous(), { signal: this._abortController.signal });
      this.addEventListener("control:next", () => this.next(), { signal: this._abortController.signal });
      this.addEventListener("control:select", (event) => this.select(event.detail.index), { signal: this._abortController.signal });
    }
    if (this.selectedIndex === 0) {
      this._dispatchEvent("carousel:select", 0);
    } else {
      this.select(this.selectedIndex, { animate: false, force: true });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get items() {
    if (this.hasAttribute("reversed")) {
      return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children).reverse();
    } else {
      return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children);
    }
  }
  get visibleItems() {
    return this.items.filter((item) => item.offsetParent !== null);
  }
  get selectedIndex() {
    return this._selectedIndex = this._selectedIndex ?? parseInt(this.getAttribute("initial-index") || 0);
  }
  get selectedIndexAmongVisible() {
    return this.visibleItems.indexOf(this.selectedSlide);
  }
  get loop() {
    return false;
  }
  get selectedSlide() {
    return this.items[this.selectedIndex];
  }
  get previousSlide() {
    return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible - 1 + this.visibleItems.length) % this.visibleItems.length : Math.max(this.selectedIndexAmongVisible - 1, 0)];
  }
  get nextSlide() {
    return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible + 1 + this.visibleItems.length) % this.visibleItems.length : Math.min(this.selectedIndexAmongVisible + 1, this.visibleItems.length - 1)];
  }
  previous(animate = true) {
    this.select(this.items.indexOf(this.previousSlide), { direction: "previous", animate });
  }
  next(animate = true) {
    this.select(this.items.indexOf(this.nextSlide), { direction: "next", animate });
  }
  _transitionTo(fromSlide, toSlide, options = {}) {}
  _adjustHeight() {
    if (this.hasAttribute("adaptive-height") && this.selectedSlide.clientHeight !== this.clientHeight) {
      this.style.maxHeight = `${this.selectedSlide.clientHeight}px`;
    }
  }
  _filterItems(event) {
    this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
  }
  _preloadImages() {
    [this.previousSlide, this.nextSlide].forEach((item) => {
      if (item) {
        Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.setAttribute("loading", "eager"));
      }
    });
  }
  _dispatchEvent(eventName, index) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { slide: this.items[index], index } }));
  }
}

function setBtnState(btn, show) {
  if (!btn) return;
  if (show) {
    btn.removeAttribute("disabled");
    btn.classList.remove("is-disabled", "is-hidden");
    btn.style.setProperty("display", "flex", "important");
    btn.style.setProperty("opacity", "1", "important");
    btn.style.setProperty("visibility", "visible", "important");
    btn.style.setProperty("pointer-events", "auto", "important");
  } else {
    btn.setAttribute("disabled", "disabled");
    btn.classList.add("is-disabled", "is-hidden");
    btn.style.setProperty("display", "none", "important");
    btn.style.setProperty("opacity", "0", "important");
    btn.style.setProperty("visibility", "hidden", "important");
    btn.style.setProperty("pointer-events", "none", "important");
  }
}

function updateCarouselButtons(carousel) {
  if (!carousel) return;
  const container = carousel.closest(".floating-controls-container") || carousel.closest(".shopify-section") || carousel.parentElement;
  if (!container) return;

  const targetId = carousel.id;
  let buttons = [];
  if (targetId) {
    buttons = Array.from(document.querySelectorAll(`button[aria-controls="${targetId}"]`));
  }
  if (buttons.length < 2) {
    const containerBtns = Array.from(container.querySelectorAll("button[is='prev-button'], button[is='next-button'], .circle-button"));
    buttons = Array.from(new Set(buttons.concat(containerBtns)));
  }

  let prevBtn = buttons.find(b => b.matches("[is='prev-button'], .circle-button--prev") || b.querySelector(".animated-arrow--reverse"));
  let nextBtn = buttons.find(b => b.matches("[is='next-button'], .circle-button--next") || (b.querySelector(".animated-arrow") && !b.querySelector(".animated-arrow--reverse")));

  if (!prevBtn && buttons.length > 0) prevBtn = buttons[0];
  if (!nextBtn && buttons.length > 1) nextBtn = buttons[1];

  if (!prevBtn && !nextBtn) return;

  const inner = carousel.querySelector(".product-list, .product-grid, ul, .grid");

  const cScrollLeft = carousel.scrollLeft || 0;
  const iScrollLeft = inner ? (inner.scrollLeft || 0) : 0;
  const scrollLeft = Math.round(Math.max(cScrollLeft, iScrollLeft));

  const cMaxScroll = carousel.scrollWidth - carousel.clientWidth;
  const iMaxScroll = inner ? (inner.scrollWidth - inner.clientWidth) : 0;
  const maxScroll = Math.round(Math.max(cMaxScroll, iMaxScroll));

  if (maxScroll <= 5) {
    setBtnState(prevBtn, false);
    setBtnState(nextBtn, false);
    return;
  }

  const isAtStart = scrollLeft <= 5;
  const isAtEnd = scrollLeft >= (maxScroll - 8);

  setBtnState(prevBtn, !isAtStart);
  setBtnState(nextBtn, !isAtEnd);
}

window.updateCarouselButtons = updateCarouselButtons;

function animateScrollCheck(carousel) {
  if (!carousel) return;
  let lastScrollLeft = -1;
  let countUnchanged = 0;

  const step = () => {
    updateCarouselButtons(carousel);
    const currentScrollLeft = Math.round(carousel.scrollLeft);
    if (currentScrollLeft === lastScrollLeft) {
      countUnchanged++;
    } else {
      countUnchanged = 0;
      lastScrollLeft = currentScrollLeft;
    }

    if (countUnchanged < 15) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
}

window.animateScrollCheck = animateScrollCheck;

// Scroll Carousel Custom Element
class ScrollCarousel extends BaseCarousel {
  constructor() {
    super();
    if (window.ResizeObserver) {
      new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
    }
  }
  connectedCallback() {
    this._hasPendingProgrammaticScroll = false;
    this._scrollArea = new ScrollArea(this, this._abortController);
    super.connectedCallback();

    const handleScroll = () => {
      this._onCarouselScroll();
      updateCarouselButtons(this);
    };

    const inner = this.querySelector(".product-list, .product-grid, ul, .grid");

    this.addEventListener("scroll", handleScroll, { signal: this._abortController.signal, passive: true });
    this.addEventListener("scrollend", () => {
      this._onScrollSettled();
      updateCarouselButtons(this);
    }, { signal: this._abortController.signal });

    if (inner) {
      inner.addEventListener("scroll", handleScroll, { signal: this._abortController.signal, passive: true });
      inner.addEventListener("scrollend", () => updateCarouselButtons(this), { signal: this._abortController.signal });
    }

    if (window.ResizeObserver) {
      new ResizeObserver(() => updateCarouselButtons(this)).observe(this);
      if (inner) new ResizeObserver(() => updateCarouselButtons(this)).observe(inner);
    }

    animateScrollCheck(this);
  }
  get itemOffset() {
    const items = this.visibleItems;
    if (items.length < 2) return this.clientWidth * 0.75;
    const first = items[0], second = items[1];
    const offset = second.offsetLeft - first.offsetLeft;
    return offset > 0 ? offset : (first.getBoundingClientRect().width || (this.clientWidth * 0.75));
  }
  get slidesPerPage() {
    return this.visibleItems.length < 2 ? 1 : Math.max(1, Math.floor(this.clientWidth / Math.max(1, this.itemOffset)));
  }
  get totalPages() {
    return this.visibleItems.length < 2 ? 1 : Math.max(1, this.visibleItems.length - this.slidesPerPage + 1);
  }
  next(animate = true) {
    const step = this.itemOffset || (this.clientWidth * 0.75);
    const targetScroll = this.scrollLeft + step;
    const maxScroll = Math.max(0, this.scrollWidth - this.clientWidth);
    const scrollAmount = Math.min(targetScroll, maxScroll);
    this.scrollTo({ left: scrollAmount, behavior: animate ? "smooth" : "auto" });
    const inner = this.querySelector(".product-list, .product-grid, ul, .grid");
    if (inner && inner.scrollWidth > inner.clientWidth) {
      inner.scrollBy({ left: step, behavior: animate ? "smooth" : "auto" });
    }
    animateScrollCheck(this);
  }
  previous(animate = true) {
    const step = this.itemOffset || (this.clientWidth * 0.75);
    const scrollAmount = Math.max(0, this.scrollLeft - step);
    this.scrollTo({ left: scrollAmount, behavior: animate ? "smooth" : "auto" });
    const inner = this.querySelector(".product-list, .product-grid, ul, .grid");
    if (inner && inner.scrollWidth > inner.clientWidth) {
      inner.scrollBy({ left: -step, behavior: animate ? "smooth" : "auto" });
    }
    animateScrollCheck(this);
  }
  select(index, { animate = true, force = false } = {}) {
    const step = this.itemOffset || (this.clientWidth * 0.75);
    const scrollAmount = index * step;
    this._selectedIndex = index;
    this._dispatchEvent("carousel:select", index);
    this.scrollTo({ left: scrollAmount, behavior: animate ? "smooth" : "auto" });
    animateScrollCheck(this);
  }
  _transitionTo(fromSlide, toSlide, { animate = true } = {}) {
    if (fromSlide) fromSlide.classList.remove("is-selected");
    if (toSlide) toSlide.classList.add("is-selected");
    const step = this.itemOffset || (this.clientWidth * 0.75);
    const targetIndex = this.visibleItems.indexOf(toSlide);
    const scrollAmount = targetIndex >= 0 ? targetIndex * step : 0;
    this._hasPendingProgrammaticScroll = animate;
    this.scrollTo({ left: scrollAmount, behavior: animate ? "smooth" : "auto" });
    animateScrollCheck(this);
  }
  _onCarouselScroll() {
    if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === "none") {
      return;
    }
    const newIndex = Math.max(0, Math.round(this.scrollLeft / (this.itemOffset || 1)));
    if (newIndex !== this.selectedIndex) {
      this._selectedIndex = newIndex;
      this._dispatchEvent("carousel:select", this.selectedIndex);
      this._dispatchEvent("carousel:change", this.selectedIndex);
    }
    updateCarouselButtons(this);
  }
  _onScrollSettled() {
    this._hasPendingProgrammaticScroll = false;
    this._dispatchEvent("carousel:settle", this.selectedIndex);
    updateCarouselButtons(this);
  }
  _adjustHeight() {
    this.style.maxHeight = null;
    if (this._scrollArea.scrollDirection !== "none") {
      super._adjustHeight();
    }
    updateCarouselButtons(this);
  }
  _extractSlideAlign(slide) {
    return getComputedStyle(slide).scrollSnapAlign === "center" ? "center" : "start";
  }
}
if (!window.customElements.get("scroll-carousel")) {
  window.customElements.define("scroll-carousel", ScrollCarousel);
}

// Prev Button
class PrevButton extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const target = this.controlledElement;
      if (target) {
        if (typeof target.previous === "function") {
          target.previous();
        } else {
          target.scrollBy({ left: -target.clientWidth * 0.75, behavior: "smooth" });
        }
        animateScrollCheck(target);
      }
    }, { signal: this._abortController.signal });
  }
  disconnectedCallback() {
    this._abortController?.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
}
if (!window.customElements.get("prev-button")) {
  window.customElements.define("prev-button", PrevButton, { extends: "button" });
}

// Next Button
class NextButton extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const target = this.controlledElement;
      if (target) {
        if (typeof target.next === "function") {
          target.next();
        } else {
          target.scrollBy({ left: target.clientWidth * 0.75, behavior: "smooth" });
        }
        animateScrollCheck(target);
      }
    }, { signal: this._abortController.signal });
  }
  disconnectedCallback() {
    this._abortController?.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
}
if (!window.customElements.get("next-button")) {
  window.customElements.define("next-button", NextButton, { extends: "button" });
}

// Page Dots
class PageDots extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.items = Array.from(this.children);
    this.items.forEach((button, index) => button.addEventListener("click", () => this.select(index), { signal: this._abortController.signal }));
    this.addEventListener("control:filter", this._filterItems, { signal: this._abortController.signal });
    if (this.controlledElement) {
      this.controlledElement.addEventListener("carousel:change", (event) => this.select(event.detail.index, false), { signal: this._abortController.signal });
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target)));
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
  get selectedIndex() {
    return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
  }
  select(selectedIndex, dispatchEvent = true) {
    if (this.hasAttribute("align-selected")) {
      this.scrollTo({
        left: this.items[selectedIndex].offsetLeft - this.clientWidth / 2 + this.items[selectedIndex].clientWidth / 2,
        top: this.items[selectedIndex].offsetTop - this.clientHeight / 2 - this.items[selectedIndex].clientHeight / 2,
        behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto"
      });
    }
    if (this.selectedIndex === selectedIndex) {
      return;
    }
    this.items.forEach((button, index) => button.setAttribute("aria-current", selectedIndex === index ? "true" : "false"));
    if (dispatchEvent) {
      this._dispatchEvent(selectedIndex);
    }
  }
  _filterItems(event) {
    this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
  }
  _dispatchEvent(index) {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:select", { bubbles: true, cancelable: true, detail: { index } }));
  }
}
if (!window.customElements.get("page-dots")) {
  window.customElements.define("page-dots", PageDots);
}

// Collection List scroll reveal animation
class CollectionList extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      if (window.IntersectionObserver) {
        this._observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-revealed");
              this._observer.unobserve(entry.target);
            }
          });
        }, { rootMargin: "-100px" });
        this.querySelectorAll("[reveal-js]").forEach((item) => this._observer.observe(item));
      } else {
        this.querySelectorAll("[reveal-js]").forEach((item) => item.classList.add("is-revealed"));
      }
    } else {
      this.querySelectorAll("[reveal-js]").forEach((item) => item.classList.add("is-revealed"));
    }
  }
  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }
}
if (!window.customElements.get("collection-list")) {
  window.customElements.define("collection-list", CollectionList);
}

// Global fallback capture event listener for Carousel control buttons
document.addEventListener("click", (event) => {
  const btn = event.target.closest("button[is='prev-button'], button[is='next-button'], .circle-button, [aria-controls]");
  if (!btn) return;
  const isPrev = btn.matches("[is='prev-button'], .circle-button--prev") || btn.querySelector(".animated-arrow--reverse");
  const isNext = btn.matches("[is='next-button'], .circle-button--next") || (btn.querySelector(".animated-arrow") && !btn.querySelector(".animated-arrow--reverse"));
  if (!isPrev && !isNext) return;

  const targetId = btn.getAttribute("aria-controls");
  const carousel = targetId ? document.getElementById(targetId) : btn.closest(".floating-controls-container, .shopify-section")?.querySelector("scroll-carousel");
  if (!carousel) return;

  event.preventDefault();
  event.stopPropagation();

  const step = carousel.itemOffset || (carousel.clientWidth * 0.75);
  const inner = carousel.querySelector(".product-list, .product-grid, ul, .grid");

  if (isPrev) {
    if (typeof carousel.previous === "function") {
      carousel.previous();
    } else {
      carousel.scrollBy({ left: -step, behavior: "smooth" });
      if (inner) inner.scrollBy({ left: -step, behavior: "smooth" });
    }
  } else if (isNext) {
    if (typeof carousel.next === "function") {
      carousel.next();
    } else {
      carousel.scrollBy({ left: step, behavior: "smooth" });
      if (inner) inner.scrollBy({ left: step, behavior: "smooth" });
    }
  }

  animateScrollCheck(carousel);
}, true);
